// src/modules/workspace/wsShared.js
// ─────────────────────────────────────────────────────────────────────────────
// Shared: design tokens · data utilities · tiny UI atoms
// Imported by all workspace sub-files — no external deps
// ─────────────────────────────────────────────────────────────────────────────
import React from 'react';

// ── Design tokens ─────────────────────────────────────────────────────────────
export const C = {
  void:'#020508', deep:'#060b14', surface:'#0a1120', raised:'#0f1826',
  b0:'#0f1a2e', b1:'#1e293b', b2:'#2d3f55',
  t1:'#e2e8f0', t2:'#b6c3d8', t3:'#8396b3', t4:'#627795',
  cyan:'#22d3ee', green:'#4ade80', gold:'#fbbf24', red:'#f87171',
  violet:'#a78bfa', orange:'#fb923c', pink:'#f472b6', blue:'#60a5fa',
};
// PALETTE is a live mutable array synced by ChartPaletteContext.
// All consumers that import PALETTE will read the current user-selected palette.
import { _livePalette, subscribePalette, getPaletteVersion } from '../../context/paletteSync';
export const PALETTE = _livePalette;

// Re-export palette reactivity hook for workspace labs.
// Use `pv` in useMemo dependency arrays to bust stale chart option caches.
export { subscribePalette, getPaletteVersion };
import { useState, useEffect } from 'react';
export function usePaletteVersion() {
  const [ver, setVer] = useState(getPaletteVersion);
  useEffect(() => subscribePalette((_, v) => setVer(v)), []);
  return ver;
}

// ── Analytics sampling (interactive tab operations only) ─────────────────────
// The Web Worker (edaWorker.js) processes 100% of rows for the initial profile.
// These functions are called on the MAIN THREAD by DistributionLab, StatisticsLab
// etc. for interactive operations (column selectors, bin sliders). On 1M rows,
// sorting or iterating the full array in a useMemo would freeze the UI for seconds.
// 500K gives statistically identical results to 1M and keeps interactions < 200ms.
export const ANALYTICS_CAP = 500_000;
function sampleForAnalysis(rows) {
  if (rows.length <= ANALYTICS_CAP) return rows;
  const step = Math.ceil(rows.length / ANALYTICS_CAP);
  return rows.filter((_, i) => i % step === 0);
}

// ── CSV parser (handles quotes) ───────────────────────────────────────────────
export function parseCSV(text) {
  const splitLine = (line) => {
    const r=[]; let cur='', inQ=false;
    for(const ch of line){ if(ch==='"') inQ=!inQ; else if(ch===','&&!inQ){r.push(cur.trim());cur='';} else cur+=ch; }
    r.push(cur.trim()); return r;
  };
  const lines = text.trim().split('\n').filter(l=>l.trim());
  if(lines.length<2) return null;
  const headers = splitLine(lines[0]).map(h=>h.replace(/"/g,'').trim());
  const rows = lines.slice(1).map(line=>{
    const vals=splitLine(line);
    const obj={}; headers.forEach((h,i)=>{ obj[h]=(vals[i]??'').replace(/"/g,'').trim(); });
    return obj;
  }).filter(r=>Object.values(r).some(v=>v!==''));
  return { headers, rows, shape:[rows.length, headers.length] };
}

// ── Type inference ────────────────────────────────────────────────────────────
const normalizeTypeOverride = (type) => {
  if (!type) return null;
  const t = String(type).toLowerCase();
  if (t === 'numeric' || t === 'integer') return 'numeric';
  if (t === 'categorical' || t === 'string' || t === 'boolean' || t === 'lowercase' || t === 'uppercase' || t === 'trim') return 'categorical';
  return null;
};

export function inferTypes(headers, rows) {
  const typeOverrides = rows?.__typeOverrides || {};
  // Sample for type detection — 10K rows is more than enough to detect types accurately.
  // uniqueCount is relative to the sample; profileDataset adjusts flag thresholds accordingly.
  const data = rows.length > 10_000 ? rows.filter((_, i) => i % Math.ceil(rows.length / 10_000) === 0) : rows;
  return headers.map(col => {
    const vals = data.map(r=>r[col]).filter(v=>v!==''&&v!=null);
    const nums = vals.filter(v=>!isNaN(parseFloat(v))&&isFinite(v));
    const isNum = nums.length/Math.max(vals.length,1) > 0.8;
    const forcedType = normalizeTypeOverride(typeOverrides[col]);
    // sampleSize lets callers scale uniqueCount ratios correctly
    return { col, type: forcedType || (isNum?'numeric':'categorical'), uniqueCount: new Set(vals).size, nullCount: rows.length-vals.length, sampleSize: data.length };
  });
}

// ── Column stats ──────────────────────────────────────────────────────────────
export function colStats(rows, col) {
  const data = sampleForAnalysis(rows);
  const vals = data.map(r=>parseFloat(r[col])).filter(v=>!isNaN(v)&&isFinite(v));
  if(!vals.length) return null;
  const s = [...vals].sort((a,b)=>a-b);
  const n=s.length, mean=vals.reduce((a,b)=>a+b,0)/n;
  const variance=vals.reduce((a,v)=>a+(v-mean)**2,0)/n;
  const std=Math.sqrt(variance);
  const q1=s[Math.floor(n*.25)], median=s[Math.floor(n*.5)], q3=s[Math.floor(n*.75)];
  const iqr=q3-q1;
  const skew = std>0 ? (mean-median)/std : 0;
  const outlierCount = vals.filter(v=>v<q1-1.5*iqr||v>q3+1.5*iqr).length;
  // nulls is estimated from sample; for exact null counts use the nullMap in profileDataset
  return { mean:+mean.toFixed(4), std:+std.toFixed(4), min:s[0], max:s[n-1], q1, median, q3, iqr:+iqr.toFixed(4), skew:+skew.toFixed(3), count:n, nulls:data.length-n, outlierCount, sampled: data.length < rows.length };
}

// ── Histogram ─────────────────────────────────────────────────────────────────
export function getHistogram(rows, col, bins=20) {
  const data = sampleForAnalysis(rows);
  const vals = data.map(r=>parseFloat(r[col])).filter(v=>!isNaN(v)&&isFinite(v));
  if(!vals.length) return [];
  // NOTE: Never use Math.min(...vals)/Math.max(...vals) with large arrays — the spread
  // operator blows the JS call stack at ~125K elements. Always use a for-loop.
  let mn = vals[0], mx = vals[0];
  for (let i = 1; i < vals.length; i++) {
    if (vals[i] < mn) mn = vals[i];
    if (vals[i] > mx) mx = vals[i];
  }
  const step = (mx - mn) / bins || 1;
  const buckets=Array.from({length:bins},(_,i)=>({label:+(mn+i*step).toFixed(3),count:0}));
  vals.forEach(v=>{ const idx=Math.min(Math.floor((v-mn)/step),bins-1); buckets[idx].count++; });
  return buckets;
}

// ── Value counts ──────────────────────────────────────────────────────────────
export function getValueCounts(rows, col, top=20) {
  const data = sampleForAnalysis(rows);
  const counts={};
  data.forEach(r=>{ const v=r[col]; if(v!==''&&v!=null) counts[v]=(counts[v]||0)+1; });
  return Object.entries(counts).sort((a,b)=>b[1]-a[1]).slice(0,top)
    .map(([name,count])=>({ name, count, pct:+(count/data.length*100).toFixed(1) }));
}

// ── Pearson correlation ───────────────────────────────────────────────────────
export function pearson(rows, a, b) {
  const pairs=rows.map(r=>[parseFloat(r[a]),parseFloat(r[b])]).filter(([x,y])=>!isNaN(x)&&!isNaN(y)&&isFinite(x)&&isFinite(y));
  if(pairs.length<3) return 0;
  const n=pairs.length, ma=pairs.reduce((s,[x])=>s+x,0)/n, mb=pairs.reduce((s,[,y])=>s+y,0)/n;
  const num=pairs.reduce((s,[x,y])=>s+(x-ma)*(y-mb),0);
  const da=Math.sqrt(pairs.reduce((s,[x])=>s+(x-ma)**2,0)), db=Math.sqrt(pairs.reduce((s,[,y])=>s+(y-mb)**2,0));
  return da*db===0?0:+(num/(da*db)).toFixed(3);
}

// ─────────────────────────────────────────────────────────────────────────────
// UI ATOMS — used across all workspace files
// ─────────────────────────────────────────────────────────────────────────────

export const Btn = ({ children, onClick, color=C.cyan, small, disabled, variant='outline', style={} }) => (
  <button onClick={disabled?undefined:onClick} disabled={disabled} style={{
    padding: small?'5px 11px':'8px 18px',
    border:`1px solid ${disabled?C.b1:color}`,
    background: variant==='fill'?color:color+'18',
    color: variant==='fill'?C.void:disabled?C.t4:color,
    fontFamily:'JetBrains Mono,monospace', fontSize:small?9:10,
    letterSpacing:'.09em', fontWeight:700, borderRadius:3,
    cursor:disabled?'not-allowed':'pointer', opacity:disabled?.5:1,
    display:'inline-flex', alignItems:'center', gap:5, transition:'all .12s', ...style,
  }}>{children}</button>
);

export const Label = ({ children }) => (
  <div style={{ fontSize:9, color:C.t3, letterSpacing:'.12em', marginBottom:4, fontFamily:'JetBrains Mono,monospace' }}>{children}</div>
);

export const Card = ({ children, style={}, accent }) => (
  <div style={{ background:C.surface, border:`1px solid ${accent?accent+'33':C.b1}`, borderRadius:4, overflow:'hidden', ...style }}>{children}</div>
);

export const CardHead = ({ label, accent=C.cyan, right }) => (
  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'9px 14px', borderBottom:`1px solid ${C.b0}`, background:C.deep }}>
    <span style={{ fontSize:10, color:accent, letterSpacing:'.13em', fontWeight:700, fontFamily:'JetBrains Mono,monospace' }}>{label}</span>
    {right}
  </div>
);

export const MetricTile = ({ label, value, color=C.cyan, sub }) => (
  <div style={{ background:C.raised, border:`1px solid ${C.b1}`, borderLeft:`2px solid ${color}`, padding:'10px 12px', borderRadius:3 }}>
    <div style={{ fontSize:8, color:C.t4, letterSpacing:'.12em' }}>{label}</div>
    <div style={{ fontFamily:'Syne,sans-serif', fontWeight:800, fontSize:18, color }}>{value}</div>
    {sub&&<div style={{ fontSize:9, color:C.t3, marginTop:1 }}>{sub}</div>}
  </div>
);

export const Sel = ({ value, onChange, options, label, style={} }) => (
  <div>
    {label&&<Label>{label}</Label>}
    <select value={value} onChange={e=>onChange(e.target.value)} style={{
      background:C.deep, border:`1px solid ${C.b2}`, color:C.t1,
      padding:'6px 9px', fontSize:11, fontFamily:'JetBrains Mono,monospace',
      borderRadius:3, cursor:'pointer', outline:'none', ...style,
    }}>
      {options.map(o=><option key={o.value??o} value={o.value??o}>{o.label??o}</option>)}
    </select>
  </div>
);

export const TxtInput = ({ value, onChange, placeholder, style={}, type='text' }) => (
  <input value={value} type={type} onChange={e=>onChange(e.target.value)} placeholder={placeholder} style={{
    background:C.deep, border:`1px solid ${C.b2}`, color:C.t1,
    padding:'6px 9px', fontSize:11, fontFamily:'JetBrains Mono,monospace',
    borderRadius:3, outline:'none', ...style,
  }} />
);

export const ChartTip = ({ active, payload, label }) => {
  if(!active||!payload?.length) return null;
  return (
    <div style={{ background:C.surface, border:`1px solid ${C.b2}`, padding:'7px 11px', borderRadius:3, fontSize:11 }}>
      <div style={{ color:C.t3, marginBottom:3 }}>{label}</div>
      {payload.map((p,i)=><div key={i} style={{ color:p.color||C.cyan }}>{p.name??'value'}: {typeof p.value==='number'?p.value.toFixed(4):p.value}</div>)}
    </div>
  );
};

export const SectionToggle = ({ title, accent=C.cyan, children, defaultOpen=true }) => {
  const [open,setOpen] = React.useState(defaultOpen);
  return (
    <div style={{ background:C.surface, border:`1px solid ${C.b1}`, borderRadius:4, overflow:'hidden' }}>
      <div onClick={()=>setOpen(o=>!o)} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'9px 14px', background:C.deep, cursor:'pointer', borderBottom: open?`1px solid ${C.b0}`:'none' }}>
        <span style={{ fontSize:10, color:accent, letterSpacing:'.13em', fontWeight:700, fontFamily:'JetBrains Mono,monospace' }}>{title}</span>
        <span style={{ color:C.t4, fontSize:11 }}>{open?'▲':'▼'}</span>
      </div>
      {open&&<div style={{ padding:14 }}>{children}</div>}
    </div>
  );
};

// ── Inline CSS injected once ──────────────────────────────────────────────────
export const WS_GLOBAL_STYLE = `
  *{box-sizing:border-box}
  ::-webkit-scrollbar{width:5px;height:5px}
  ::-webkit-scrollbar-track{background:transparent}
  ::-webkit-scrollbar-thumb{background:#1e293b;border-radius:3px}
  ::-webkit-scrollbar-thumb:hover{background:#2d3f55}
  @keyframes blink{0%,100%{opacity:1}50%{opacity:0}}
`;

// ── Pin Context (shared across all labs) ─────────────────────────────────────
// Usage: wrap ExperimentDetail with <PinProvider onPin={fn}>
// Any child lab calls: const { pinChart, pinnedIds } = usePinContext()
export const PinContext = React.createContext({ pinChart: () => {}, pinnedIds: new Set() });
export const usePinContext = () => React.useContext(PinContext);

// ── PinButton — universal ☆/★ button used in every lab ───────────────────────
export function PinButton({ chart, small }) {
  const { pinChart, pinnedIds } = React.useContext(PinContext);
  const pinned = pinnedIds.has(chart?.id);
  return (
    <button
      title={pinned ? '★ Pinned to Report' : '☆ Pin to Report'}
      onClick={e => { e.stopPropagation(); pinChart(chart); }}
      style={{
        width: small ? 20 : 24, height: small ? 20 : 24,
        background: pinned ? C.gold + '28' : 'none',
        border: `1px solid ${pinned ? C.gold : C.b1}`,
        color: pinned ? C.gold : C.t4,
        borderRadius: 2, cursor: 'pointer', fontSize: small ? 10 : 12,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, padding: 0, transition: 'all .15s',
      }}
    >{pinned ? '★' : '☆'}</button>
  );
}

// ── LabLoader — shown while a lab's heavy computation is running ──────────────
// Usage: if (!data) return <LabLoader rows={rows.length} label="COMPUTING STATS" />;
const labSpinStyle = {
  width: 28, height: 28, border: `2px solid ${C.b1}`,
  borderTop: `2px solid ${C.cyan}`, borderRadius: '50%',
  animation: 'eda-spin .7s linear infinite',
};
export function LabLoader({ rows, label }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center',
      justifyContent:'center', padding:'60px 20px', gap:14 }}>
      <div style={labSpinStyle}/>
      <div style={{ fontFamily:'JetBrains Mono,monospace', fontSize:10,
        color:C.cyan, letterSpacing:'.12em' }}>
        {label || 'COMPUTING…'}
      </div>
      {rows != null && (
        <div style={{ fontSize:9, color:C.t4, fontFamily:'JetBrains Mono,monospace' }}>
          {Number(rows).toLocaleString()} rows · results appear shortly
        </div>
      )}
    </div>
  );
}
